import {
  type RepositoryFactory,
  type SubscriptionRepository,
  type CategoryRepository,
  type PaymentHistoryRepository,
  type LifecycleEventRepository,
  type SubscriptionPeriodRepository,
  type FxRateRepository,
} from "./interfaces";
import { SupabaseCategoryRepository } from "./supabase/CategoryRepository";
import { SupabaseLifecycleEventRepository } from "./supabase/LifecycleEventRepository";
import { SupabasePaymentHistoryRepository } from "./supabase/PaymentHistoryRepository";
import { SupabaseSubscriptionPeriodRepository } from "./supabase/SubscriptionPeriodRepository";
import { SupabaseSubscriptionRepository } from "./supabase/SubscriptionRepository";
import { SupabaseFxRateRepository } from "./supabase/SupabaseFxRateRepository";
import {
  SupabaseUserSettingsRepository,
  type UserSettingsRepository,
} from "./supabase/UserSettingsRepository";
import { SupabaseFxRateManager } from "@/integrations/fx/SupabaseFxRateManager";
import { type FxRateManager } from "@/types/fx";

class DefaultRepositoryFactory implements RepositoryFactory {
  private subscriptionRepository: SubscriptionRepository;
  private categoryRepository: CategoryRepository;
  private paymentHistoryRepository: PaymentHistoryRepository;
  private userSettingsRepository: UserSettingsRepository;
  private lifecycleEventRepository: LifecycleEventRepository;
  private subscriptionPeriodRepository: SubscriptionPeriodRepository;
  private fxRateRepository: FxRateRepository;
  private fxRateManager: FxRateManager;

  constructor() {
    // Use Supabase repositories for all data persistence
    this.subscriptionRepository = new SupabaseSubscriptionRepository();
    this.categoryRepository = new SupabaseCategoryRepository();
    this.paymentHistoryRepository = new SupabasePaymentHistoryRepository();
    this.userSettingsRepository = new SupabaseUserSettingsRepository();
    this.lifecycleEventRepository = new SupabaseLifecycleEventRepository();
    this.subscriptionPeriodRepository =
      new SupabaseSubscriptionPeriodRepository();

    // FX rates also use Supabase for consistency
    this.fxRateRepository = new SupabaseFxRateRepository();
    this.fxRateManager = new SupabaseFxRateManager(this.fxRateRepository);
  }

  getSubscriptionRepository(): SubscriptionRepository {
    return this.subscriptionRepository;
  }

  getCategoryRepository(): CategoryRepository {
    return this.categoryRepository;
  }

  getPaymentHistoryRepository(): PaymentHistoryRepository {
    return this.paymentHistoryRepository;
  }

  getUserSettingsRepository(): UserSettingsRepository {
    return this.userSettingsRepository;
  }

  getLifecycleEventRepository(): LifecycleEventRepository {
    return this.lifecycleEventRepository;
  }

  getSubscriptionPeriodRepository(): SubscriptionPeriodRepository {
    return this.subscriptionPeriodRepository;
  }

  getFxRateRepository(): FxRateRepository {
    return this.fxRateRepository;
  }

  getFxRateManager(): FxRateManager {
    return this.fxRateManager;
  }
}

export const repositoryFactory = new DefaultRepositoryFactory();
